import React, { useEffect, useState } from "react";
import { Alert, Empty, Modal, Spin, Table, Tag } from "antd";
import moment from "moment";
import subscriptionApi from "../../api/subscriptionApi";
import useCompanyDateFormat from "../../hooks/useCompanyDateFormat";
import TrainingHistory from "./TrainingHistory";
import "./SubscriptionDetails.css";

const STATUS_TAG = {
  active: { color: "green", label: "Active" },
  expiring_soon: { color: "orange", label: "Expiring soon" },
  expired: { color: "red", label: "Expired" },
  no_expiry: { color: "blue", label: "No end date" },
};

const EVENT_LABEL = {
  trial: "Trial started",
  renewal: "Renewed",
  plan_change: "Plan changed",
  update: "Updated",
};

const Stat = ({ label, value }) => (
  <div style={{ background: "#f5f8fb", borderRadius: 8, padding: "12px 14px" }}>
    <div style={{ fontSize: 12, color: "#6b7785" }}>{label}</div>
    <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{value}</div>
  </div>
);

const SubscriptionDetailsModal = ({ open, onClose }) => {
  const dateFormat = useCompanyDateFormat();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [details, setDetails] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    subscriptionApi
      .getDetails()
      .then((res) => {
        if (cancelled) return;
        if (res.status !== 200) setError(res.message || "Something went wrong. Please try again.");
        else setDetails(res.data);
      })
      .catch(() => !cancelled && setError("Something went wrong. Please try again."))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  const fmt = (value) => (value && moment(value).isValid() ? moment(value).format(dateFormat) : "Not set");
  const tag = details ? STATUS_TAG[details.subscriptionStatus] || STATUS_TAG.active : null;

  const columns = [
    { title: "Event", dataIndex: "eventType", key: "eventType", render: (v) => EVENT_LABEL[v] || v },
    // { title: "Plan", dataIndex: "planName", key: "planName", render: (v) => v || "Not set" },
    { title: "Start", dataIndex: "planStartDate", key: "planStartDate", render: fmt },
    { title: "End", dataIndex: "planEndDate", key: "planEndDate", render: fmt },
    { title: "Users", dataIndex: "maxUsers", key: "maxUsers", render: (v) => v ?? "Not set" },
    { title: "Recorded on", dataIndex: "createdAt", key: "createdAt", render: fmt },
  ];

  return (
    <Modal title="Subscription Details" visible={open} onCancel={onClose} footer={null} width={760} destroyOnClose>
      <Spin spinning={loading}>
        {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
        {details && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <strong style={{ fontSize: 16 }}>{details.planName || "No Subscription"}</strong>
              <Tag color={tag.color}>{tag.label}</Tag>
              {details.subscriptionStatus === "expiring_soon" && (
                <span style={{ color: "#d46b08" }}>
                  {details.daysLeft === 0 ? "Ends today" : `${details.daysLeft} days left`}
                </span>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
              <Stat label="Start date" value={fmt(details.planStartDate)} />
              <Stat label="End date" value={fmt(details.planEndDate)} />
              <Stat label="Active users" value={details.activeUsers} />
              <Stat label="Total users (user limit)" value={details.totalUsers ?? "Not set"} />
            </div>
            <section className="sd-section">
              <div className="sd-section-head">
                <h4 className="sd-section-title">
                  Subscription history
                  {details.history.length > 0 && <span className="sd-section-count">{details.history.length}</span>}
                </h4>
              </div>
              <Table
                size="small"
                rowKey="_id"
                columns={columns}
                dataSource={details.history}
                pagination={false}
                scroll={details.history.length > 5 ? { x: true, y: 240 } : { x: true }}
                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No subscription history recorded yet" /> }}
              />
            </section>
            <TrainingHistory formatDate={fmt} />
          </>
        )}
      </Spin>
    </Modal>
  );
};

export default SubscriptionDetailsModal;
